import { useMemo, useState } from "react";
import { Check, X, Search } from "lucide-react";
import { COUNTRIES, BE_PROVINCES, FR_DEPARTMENTS, FR_BORDER_DEPARTMENTS, zoneLabel, isStructuredZone } from "@/i18n/zones";

const Chip = ({ active, onClick, children, testId, small }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1 rounded-lg border font-medium transition-colors ${small ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm"} ${
      active ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"
    }`}
    data-testid={testId}
  >
    {active && <Check className="w-3 h-3" />}{children}
  </button>
);

const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export const ZoneSelector = ({ value = [], onChange, lang = "fr", required = false }) => {
  const [openCountries, setOpenCountries] = useState(() => {
    const init = new Set();
    value.forEach((z) => { if (z.startsWith("be")) init.add("be"); if (z.startsWith("fr")) init.add("fr"); if (z === "lu") init.add("lu"); });
    return init;
  });
  const [deptSearch, setDeptSearch] = useState("");

  const has = (k) => value.includes(k);
  const toggle = (k) => onChange(has(k) ? value.filter((v) => v !== k) : [...value, k]);

  const toggleCountry = (key) => {
    const next = new Set(openCountries);
    if (next.has(key)) {
      next.delete(key);
      onChange(value.filter((v) => !(v === key || v.startsWith(`${key}:`))));
    } else {
      next.add(key);
      if (key === "lu") onChange([...value.filter((v) => v !== "lu"), "lu"]);
    }
    setOpenCountries(next);
  };

  const legacy = value.filter((v) => !isStructuredZone(v));
  const filteredDepts = useMemo(() => {
    const q = norm(deptSearch.trim());
    return q ? FR_DEPARTMENTS.filter((d) => d.code.toLowerCase().startsWith(q) || norm(d.name).includes(q)) : FR_DEPARTMENTS;
  }, [deptSearch]);

  const t = (fr, nl) => (lang === "nl" ? nl : fr);

  return (
    <div className="space-y-4" data-testid="zone-selector">
      <div className="flex flex-wrap gap-2">
        {COUNTRIES.map((c) => (
          <Chip key={c.key} active={openCountries.has(c.key)} onClick={() => toggleCountry(c.key)} testId={`zone-country-${c.key}`}>
            <span className="mr-1">{c.flag}</span>{c[lang] || c.fr}
          </Chip>
        ))}
      </div>

      {openCountries.has("be") && (
        <div className="rounded-xl border border-slate-200 p-3 sm:p-4 bg-slate-50/60" data-testid="zone-be-panel">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">🇧🇪 {t("Provinces belges", "Belgische provincies")}</p>
          <div className="flex flex-wrap gap-2">
            <Chip small active={has("be")} onClick={() => onChange(has("be") ? value.filter((v) => v !== "be") : [...value.filter((v) => !v.startsWith("be:")), "be"])} testId="zone-be-all">
              {zoneLabel("be", lang)}
            </Chip>
            {BE_PROVINCES.map((p) => (
              <Chip key={p.key} small active={has(p.key) || has("be")} onClick={() => !has("be") && toggle(p.key)} testId={`zone-${p.key.replace(":", "-")}`}>
                {p[lang] || p.fr}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {openCountries.has("lu") && (
        <div className="rounded-xl border border-slate-200 p-3 sm:p-4 bg-slate-50/60 text-sm text-slate-600" data-testid="zone-lu-panel">
          🇱🇺 {t("Tout le Grand-Duché de Luxembourg est couvert.", "Het hele Groothertogdom Luxemburg is gedekt.")}
        </div>
      )}

      {openCountries.has("fr") && (
        <div className="rounded-xl border border-slate-200 p-3 sm:p-4 bg-slate-50/60" data-testid="zone-fr-panel">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex-1">🇫🇷 {t("Départements français", "Franse departementen")}</p>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={deptSearch}
                onChange={(e) => setDeptSearch(e.target.value)}
                placeholder={t("N° ou nom du département", "Nr. of naam departement")}
                className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500 w-full sm:w-56"
                autoComplete="off"
                data-testid="zone-fr-search"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            <Chip small active={has("fr")} onClick={() => onChange(has("fr") ? value.filter((v) => v !== "fr") : [...value.filter((v) => !v.startsWith("fr:")), "fr"])} testId="zone-fr-all">
              {zoneLabel("fr", lang)}
            </Chip>
            {!deptSearch && (
              <Chip small active={FR_BORDER_DEPARTMENTS.every((c) => has(`fr:${c}`)) || has("fr")}
                onClick={() => !has("fr") && onChange([...new Set([...value, ...FR_BORDER_DEPARTMENTS.map((c) => `fr:${c}`)])])}
                testId="zone-fr-border">
                {t("Départements frontaliers", "Grensdepartementen")}
              </Chip>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5 max-h-56 overflow-y-auto pr-1">
            {filteredDepts.map((d) => (
              <Chip key={d.key} small active={has(d.key) || has("fr")} onClick={() => !has("fr") && toggle(d.key)} testId={`zone-fr-${d.code}`}>
                <span className="font-mono text-[11px] opacity-80">{d.code}</span> {d.name}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {legacy.length > 0 && (
        <div data-testid="zone-legacy">
          <p className="text-xs text-slate-500 mb-1.5">{t("Anciennes zones (texte libre) :", "Oude zones (vrije tekst):")}</p>
          <div className="flex flex-wrap gap-2">
            {legacy.map((z) => (
              <span key={z} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs">
                {z}
                <button type="button" onClick={() => toggle(z)} className="hover:text-red-600" data-testid={`zone-legacy-remove-${z}`}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400">
        {value.length === 0
          ? (required
            ? t("Sélectionnez au moins une zone (pays, province ou département).", "Selecteer minstens één zone (land, provincie of departement).")
            : t("Aucune zone sélectionnée = vous recevez les leads de toutes les zones.", "Geen zone geselecteerd = u ontvangt leads uit alle zones."))
          : t(`${value.length} zone(s) sélectionnée(s).`, `${value.length} zone(s) geselecteerd.`)}
      </p>
    </div>
  );
};
